import { generateClient, get, isCancelError, post, put } from "aws-amplify/api";
import { getUrl, uploadData } from "aws-amplify/storage";
import { resizeProfilePicture } from "../../Utils";
import awsconfig from "../../aws-exports";
import {
  confirmResetPassword,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
  signUp,
  updatePassword,
  confirmSignUp,
  confirmSignIn,
  autoSignIn,
} from "aws-amplify/auth";
import { deleteFirebaseToken } from "../../firebase";
import { clearStoredCart } from "../checkout/actions";

const client = generateClient();

export async function checkUser(dispatch) {
  try {
    const { username, signInDetails } = await getCurrentUser();

    dispatch({
      type: "LOGIN_SUCCESS",
      payload: { username, ...signInDetails },
    });
  } catch (error) {
    dispatch({ type: "LOGOUT_SUCCESS" });
  }
}

export async function requestGetUser(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_GET_USER" });
    const { sub } = payload;
    const res = await client.graphql({
      query: getUser,
      variables: { id: sub },
    });
    const user = res.data.getUser;
    if (user) {
      user.imageUrl = null;
      if (user.image?.key) {
        const { identityId, key } = user.image;
        try {
          const urlResult = await getUrl({
            path: `protected/${identityId}/${key}`,
            options: {
              useAccelerateEndpoint: true,
            },
          });
          const signedUrl = urlResult.url.href;
          user.imageUrl = signedUrl;
        } catch (error) {}
      }
      dispatch({ type: "GET_USER_SUCCESS", payload: user });
    }
  } catch (error) {
    dispatch({ type: "GET_USER_ERROR", error: error });
  }
}

export async function requestUpdateUser(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_UPDATE_USER" });
    const { input } = payload;

    if (input.image) {
      const file = await resizeProfilePicture(input.image);

      delete input.image;

      const key = `profile/${file.name}`;

      const upload = uploadData({
        path: ({ identityId }) => `protected/${identityId}/${key}`,
        data: file,
      });

      const result = await upload.result;

      if (result.path) {
        input.image = {
          key,
          bucket: awsconfig.aws_user_files_s3_bucket,
          region: awsconfig.aws_user_files_s3_bucket_region,
        };
      }
    }

    const request = post({
      apiName: "user",
      path: "/update",
      options: {
        body: input,
      },
    });

    const { body } = await request.response;
    const res = await body.json();

    const { user } = res.data;

    user.imageUrl = null;

    if (user.image?.key) {
      const { identityId, key } = user.image;
      try {
        const urlResult = await getUrl({
          path: `protected/${identityId}/${key}`,
          options: {
            useAccelerateEndpoint: true,
          },
        });
        const signedUrl = urlResult.url.href;
        user.imageUrl = signedUrl;
      } catch (error) {}
    }

    dispatch({ type: "GET_USER_SUCCESS", payload: user });
    dispatch({ type: "UPDATE_USER_SUCCESS", payload: "SUCCESS" });
  } catch (error) {
    dispatch({ type: "UPDATE_USER_ERROR", error: error });
  }
}

export async function loginUser(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_LOGIN" });
    const { email, password } = payload;
    const user = await signIn({
      username: email,
      password,
      options: {
        authFlowType: "USER_PASSWORD_AUTH",
      },
    });
    dispatch({
      type: "COGNITO_LOGIN_SUCCESS",
      payload: { cognitoUser: user },
    });
  } catch (error) {
    dispatch({ type: "LOGIN_ERROR", error: error });
  }
}

export async function requestConfirmSignIn(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_CONFIRM_LOGIN" });
    const { code } = payload;
    const loggedUser = await confirmSignIn({
      challengeResponse: code,
    });

    dispatch({ type: "CONFIRM_LOGIN_SUCCESS", payload: { loggedUser } });
  } catch (error) {
    dispatch({ type: "CONFIRM_LOGIN_ERROR", error: error });
  }
}

export async function requestAutoSignIn(dispatch) {
  try {
    dispatch({ type: "REQUEST_AUTO_SIGN_IN" });
    const loggedUser = await autoSignIn();
    dispatch({ type: "AUTO_SIGN_IN_SUCCESS", payload: { loggedUser } });
  } catch (error) {
    dispatch({ type: "AUTO_SIGN_IN_ERROR", error: error });
  }
}

export async function registerUser(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_REGISTER" });
    const { email, password = null, referral = "" } = payload;

    const params = {
      username: email,
      password,
      options: {
        userAttributes: {
          email,
        },
      },
    };

    if (!password) {
      delete params.password;
      params.options = {
        ...params.options,
        autoSignIn: {
          authFlowType: "USER_AUTH",
        },
      };
    }

    if (referral !== "") {
      params.options.userAttributes["custom:referral"] = referral;
    }

    const register = await signUp(params);
    dispatch({ type: "REGISTER_SUCCESS", payload: register });
  } catch (error) {
    dispatch({ type: "REGISTER_ERROR", error: error });
  }
}

export async function requestConfirmSignUp(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_CONFIRM_SIGNUP" });
    const { email, code } = payload;
    const confirmed = await confirmSignUp({
      username: email,
      confirmationCode: code,
    });
    dispatch({
      type: "CONFIRM_SIGNUP_SUCCESS",
      payload: confirmed,
    });
  } catch (error) {
    dispatch({ type: "CONFIRM_SIGNUP_ERROR", error: error });
  }
}

export async function requestGetEmailCode(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_GET_EMAIL_CODE" });
    const { email } = payload;
    const emailLogin = await signIn({
      username: email,
      options: {
        authFlowType: "USER_AUTH",
        preferredChallenge: "EMAIL_OTP",
      },
    });
    dispatch({
      type: "GET_EMAIL_CODE_SUCCESS",
      payload: { emailLogin },
    });
  } catch (error) {
    dispatch({ type: "GET_EMAIL_CODE_ERROR", error: error });
  }
}

export async function resendCode(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_RESEND_CODE" });
    const { email } = payload;
    const resend = await resendSignUpCode({ username: email });
    dispatch({
      type: "RESEND_CODE_SUCCESS",
      payload: resend,
    });
  } catch (error) {
    dispatch({ type: "RESEND_CODE_ERROR", error: error });
  }
}

export async function forgotPassword(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_FORGOT_PASSWORD" });
    const { email } = payload;
    const forgotPassword = await resetPassword({
      username: email,
    });
    dispatch({
      type: "FORGOT_PASSWORD_SUCCESS",
      payload: forgotPassword,
    });
  } catch (error) {
    dispatch({ type: "FORGOT_PASSWORD_ERROR", error: error });
  }
}

export async function forgotPasswordSubmit(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_PASSWORD_SUBMIT" });
    const { email, code, password } = payload;
    await confirmResetPassword({
      username: email,
      newPassword: password,
      confirmationCode: code,
    });
    dispatch({
      type: "PASSWORD_SUBMIT_SUCCESS",
    });
  } catch (error) {
    dispatch({ type: "PASSWORD_SUBMIT_ERROR", error: error });
  }
}

export async function changePassword(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_CHANGE_PASSWORD" });
    const { currentPassword, newPassword } = payload;
    const changePasswordStatus = await updatePassword({
      currentPassword,
      newPassword,
    });
    dispatch({
      type: "CHANGE_PASSWORD_SUCCESS",
      payload: changePasswordStatus,
    });
  } catch (error) {
    dispatch({ type: "CHANGE_PASSWORD_ERROR", error: error });
  }
}

export async function loginSuccess(dispatch, user) {
  try {
    localStorage.setItem("authUsername", user.username);
    dispatch({
      type: "LOGIN_SUCCESS",
      payload: { username: user.username, ...user.attributes },
    });
  } catch (error) {
    dispatch({ type: "LOGIN_ERROR", error: error });
  }
}

// Everything tied to the outgoing session. Runs on both logout paths: the
// explicit one below and logoutSuccess, which is what fires when the session
// expires or the user signs out in another tab.
const clearSessionState = async () => {
  localStorage.removeItem("authUsername");
  localStorage.removeItem("prevPage");
  clearStoredCart();
  await deleteFirebaseToken();
};

export async function logout(dispatch) {
  try {
    dispatch({ type: "REQUEST_LOGOUT" });
    await clearSessionState();
    await signOut();
  } catch (error) {
    dispatch({ type: "LOGOUT_ERROR", error: error });
  }
}

export async function logoutSuccess(dispatch) {
  try {
    await clearSessionState();
    dispatch({ type: "LOGOUT_SUCCESS" });
  } catch (error) {
    dispatch({ type: "LOGOUT_ERROR", error: error });
  }
}

// `business` (optional) is the full business record the caller already holds
// (from listBusiness / getBusiness). The REST /mainBusiness response carries a
// trimmed business, so its lifecycle fields (status, restrictedAt, graceEndsAt)
// can be missing — and an absent status reads as ACTIVE, silently unlocking
// every write gate after a switch. Merge the caller's record over it.
export async function requestAddMainBusiness(dispatch, payload) {
  try {
    const { businessID, business } = payload;
    dispatch({ type: "REQUEST_ADD_MAIN_BUSINESS", payload: { request: null } });

    const request = put({
      apiName: "user",
      path: "/mainBusiness",
      options: {
        body: {
          businessID,
        },
      },
    });

    dispatch({ type: "REQUEST_ADD_MAIN_BUSINESS", payload: { request } });

    const { body } = await request.response;
    const res = await body.json();
    const { mainBusiness, mainBusinessID } = res.data;

    const resolvedBusiness =
      business?.id && business.id === (mainBusiness?.id || businessID)
        ? { ...mainBusiness, ...business }
        : mainBusiness;

    dispatch({
      type: "SUCCESS_ADD_MAIN_BUSINESS",
      payload: { mainBusiness: resolvedBusiness, mainBusinessID },
    });
  } catch (error) {
    dispatch({ type: "ERROR_ADD_MAIN_BUSINESS", error: error });
  }
}

export async function clearNewMainBusiness(dispatch) {
  dispatch({ type: "CLEAR_NEW_MAIN_BUSINESS" });
}

export async function requestGetReferral(dispatch) {
  try {
    dispatch({
      type: "REQUEST_GET_REFERRAL",
      payload: { request: null },
    });

    const request = get({
      apiName: "referral",
      path: "/code",
    });

    dispatch({
      type: "REQUEST_GET_REFERRAL",
      payload: { request },
    });

    const response = await request.response;
    const result = await response.body.json();

    dispatch({
      type: "REQUEST_GET_REFERRAL_SUCCESS",
      payload: { referral: result },
    });
  } catch (error) {
    dispatch({ type: "REQUEST_GET_REFERRAL_ERROR", payload: { error } });
  }
}

export async function requestListReferralHistory(dispatch, payload) {
  try {
    const { page, referralID, limit, nextTokens } = payload;

    dispatch({
      type: "REQUEST_LIST_REFERRAL_HISTORY",
      payload: { request: null, page },
    });

    let items = [];
    let nextToken = nextTokens || "start";

    const params = {
      referralID,
      limit,
      sortDirection: "DESC",
    };

    while (nextToken && items.length === 0) {
      if (nextToken && nextToken !== "start") {
        params.nextToken = nextToken;
      }

      const request = client.graphql({
        query: historiesByReferralCreatedAt,
        variables: params,
      });
      dispatch({
        type: "REQUEST_LIST_REFERRAL_HISTORY",
        payload: { request, page },
      });

      // eslint-disable-next-line no-await-in-loop
      const res = await request;
      items = [...items, ...res.data.historiesByReferralCreatedAt.items];
      nextToken = res.data.historiesByReferralCreatedAt.nextToken;
    }

    const nextPage = page + 1;

    dispatch({
      type: "LIST_REFERRAL_HISTORY_SUCCESS",
      payload: {
        items,
        nextToken,
        nextPage,
      },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "LIST_REFERRAL_HISTORY_CANCEL",
      });
    } else {
      dispatch({ type: "LIST_REFERRAL_HISTORY_ERROR", error });
    }
  }
}

export async function requestListPointTransactionReferral(dispatch, payload) {
  try {
    const { page, referralID, limit, nextTokens } = payload;

    dispatch({
      type: "REQUEST_LIST_TRANSACTION_POINT_REFERRAL",
      payload: { request: null, page },
    });

    let items = [];
    let nextToken = nextTokens || "start";

    const params = {
      referralID,
      limit,
      sortDirection: "DESC",
    };

    while (nextToken && items.length === 0) {
      if (nextToken && nextToken !== "start") {
        params.nextToken = nextToken;
      }

      const request = client.graphql({
        query: pointTransactionsByReferralCreatedAt,
        variables: params,
      });
      dispatch({
        type: "REQUEST_LIST_TRANSACTION_POINT_REFERRAL",
        payload: { request, page },
      });

      // eslint-disable-next-line no-await-in-loop
      const res = await request;
      items = [
        ...items,
        ...res.data.pointTransactionsByReferralCreatedAt.items,
      ];
      nextToken = res.data.pointTransactionsByReferralCreatedAt.nextToken;
    }

    const nextPage = page + 1;

    dispatch({
      type: "LIST_TRANSACTION_POINT_REFERRAL_SUCCESS",
      payload: {
        items,
        nextToken,
        nextPage,
      },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "LIST_TRANSACTION_POINT_REFERRAL_CANCEL",
      });
    } else {
      dispatch({ type: "LIST_TRANSACTION_POINT_REFERRAL_ERROR", error });
    }
  }
}

const getUser = /* GraphQL */ `
  query GetUser($id: ID!) {
    getUser(id: $id) {
      id
      identityId
      name
      image {
        bucket
        region
        key
        identityId
      }
      email
      phone
      callBalance
      emeteraiQuota
      address {
        street
        city
        province
      }
      taxpayerInfo {
        npwp
        efin
        ptkp
        job
        ktp
      }
      notificationToken {
        web
      }
      offersNotification {
        email
        whatsapp
      }
      membership
      mainBusinessID
      mainBusiness {
        id
        name
        type
        structure
        category
        taxCategory
        taxNumber
        yearRegistered
        fiscalYearStartMonth
        address
        phone
        image {
          bucket
          region
          key
        }
        status
        restrictedAt
        graceEndsAt
        deletedAt
        inactivityWarnedAt
        lastActivityAt
        createdAt
      }
      npwpDraftID
      npwpDraft {
        id
        taxCategory
        mainNPWP
        isMain
        name
        gender
        phone
        cellPhone
        countryCode
        identityId
        familyId
        foreignerIdentityId
        birthPlace
        birthDate
        marriageStatus
        husbandCountryCode
        husbandPassport
        totalDependant
        incomeLevel
        street
        block
        streetNumber
        rtNumber
        rwNumber
        regionCode
        idStreet
        idBlock
        idStreetNumber
        idRtNumber
        idRwNumber
        idRegionCode
        businessStreet
        businessBlock
        businessStreetNumber
        businessRtNumber
        businessRwNumber
        businessRegionCode
        businessPhone
        businessCellPhone
        employeeCode
        employeeDescription
        businessCode
        businessDescription
        businessName
        businessBookeeping
        businessHasEmployee
        businessBookeepingStart
        businessBookeepingEnd
        freelanceCode
        freelanceDescription
        freelanceName
        freelanceBookeeping
        freelanceHasEmployee
        freelanceBookeepingStart
        freelanceBookeepingEnd
        otherCode
        otherDescription
      }
      registeredNpwpID
      registeredNpwp {
        id
        name
        npwp
        kppID
        kppName
        transactionID
        createdAt
        updatedAt
      }
      referralID
      referral {
        id
        rate
        pointsCap
        status
      }
      createdAt
      updatedAt
    }
  }
`;

const historiesByReferralCreatedAt = /* GraphQL */ `
  query HistoriesByReferralCreatedAt(
    $referralID: ID
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelReferralHistoryFilterInput
    $limit: Int
    $nextToken: String
  ) {
    historiesByReferralCreatedAt(
      referralID: $referralID
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        referralID
        metadata
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const pointTransactionsByReferralCreatedAt = /* GraphQL */ `
  query PointTransactionsByReferralCreatedAt(
    $referralID: ID
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelPointTransactionFilterInput
    $limit: Int
    $nextToken: String
  ) {
    pointTransactionsByReferralCreatedAt(
      referralID: $referralID
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        status
        transactionType
        total
        fee
        description
        createdAt
        expiredAt
        updatedAt
      }
      nextToken
    }
  }
`;
